import React from 'react';
import { rrulestr } from 'rrule';
import PropTypes from 'prop-types';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Pagination from '@mui/material/Pagination';
import AddIcon from '@mui/icons-material/Add';
import GrainIcon from '@mui/icons-material/Grain';
import HayIcon from '@mui/icons-material/Grass';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import EditIcon from '@mui/icons-material/Edit';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import { UserService } from '@pingum/api-services';

import {
  Badge,
  Box,
  Typography,
} from '@mui/material';
import { Medication } from '@mui/icons-material';
import SupplementIcon from '../../../../../components/CustomIcons/Pill';
import AddEditModal from './components/AddEditModal';
import FeedsCardLoading from './components/FeedsCardLoading';
import {
  isUserAdmin,
  isUserOwner,
} from '../../../../../utils/tenantAuthenticationHelper';
import { getHorseFeeds } from '../../../../../redux/horse/actions';
import {
  blueGrey,
  primary,
} from '../../../../../utils/brandColors';
import { getContrast } from '../../../../../utils/scheduleHelper';

// eslint-disable-next-line no-unused-vars
export const getIconForFeed = (feed, contrastColor = true) => {
  const color = contrastColor
    ? getContrast(feed.color || primary[500])
    : (feed.color || primary[500])
  if (feed.type === 'Grain') return <GrainIcon sx={{ color }} />;
  if (feed.type === 'Supplement') return <SupplementIcon color={color} />;
  if (feed.type === 'Medicine') return <Medication sx={{ color }} />;
  return <HayIcon sx={{ color }} />;
};

const getNumberValue = (value) => {
  if (!Number.isNaN(Number(value))) {
    return Number(value).toLocaleString();
  }
  return value;
};

const getRruleString = (rrule) => {
  if (!rrule) return 'no frequency specified';
  return rrulestr(rrule).toText();
};

const getQuantity = (horseFeed) => {
  if (!horseFeed.quantity) return 'no quantity specificed';
  return `${horseFeed.quantity} ${horseFeed.quantityMeasure}`;
};

export const getFeedDescription = (horseFeed, filterTimes) => {
  if (horseFeed.frequency.rrule.indexOf('FREQ=DAILY') >= 0) {
    return (
      <Box>
        {!!horseFeed.notes && <div><small>&#8220;{horseFeed.notes}&#8221;</small></div>}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize: 10,
            gap: 0.5,
          }}
        >
          {(!filterTimes || filterTimes.am) && <Box sx={{ flex: 1 }}>AM</Box>}
          {(!filterTimes || filterTimes.midday) && <Box sx={{ flex: 1 }}>Midday</Box>}
          {(!filterTimes || filterTimes.pm) && <Box sx={{ flex: 1 }}>PM</Box>}
          {(!filterTimes || filterTimes.evening) && <Box sx={{ flex: 1 }}>Evening</Box>}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {(!filterTimes || filterTimes.am) && <Box sx={{ flex: 1 }}>{(horseFeed.frequency.am && horseFeed.frequency.am.length > 0 && horseFeed.frequency.am !== '0' && (<Typography>{getNumberValue(horseFeed.frequency.am)} <Typography variant="caption">{horseFeed.quantityMeasure}</Typography></Typography>)) || ''}</Box>}
          {(!filterTimes || filterTimes.midday) && <Box sx={{ flex: 1 }}>{(horseFeed.frequency.midday && horseFeed.frequency.midday.length > 0 && horseFeed.frequency.midday !== '0' && (<Typography>{getNumberValue(horseFeed.frequency.midday)} <Typography variant="caption">{horseFeed.quantityMeasure}</Typography></Typography>)) || ''}</Box>}
          {(!filterTimes || filterTimes.pm) && <Box sx={{ flex: 1 }}>{(horseFeed.frequency.pm && horseFeed.frequency.pm.length > 0 && horseFeed.frequency.pm !== '0' && (<Typography>{getNumberValue(horseFeed.frequency.pm)} <Typography variant="caption">{horseFeed.quantityMeasure}</Typography></Typography>)) || ''}</Box>}
          {(!filterTimes || filterTimes.evening) && <Box sx={{ flex: 1 }}>{(horseFeed.frequency.evening && horseFeed.frequency.evening.length > 0 && horseFeed.frequency.evening !== '0' && (<Typography>{getNumberValue(horseFeed.frequency.evening)} <Typography variant="caption">{horseFeed.quantityMeasure}</Typography></Typography>)) || ''}</Box>}
        </Box>
      </Box>
    )
  }

  return (
    <>
      {`${getQuantity(horseFeed)} - ${getRruleString(horseFeed.frequency.rrule)}`}
      {!!horseFeed.notes && <div><small>&#8220;{horseFeed.notes}&#8221;</small></div>}
    </>
  )
}

export const getFeedDescriptionTableColumn = (horseFeed, filterTimes) => {
  if (horseFeed.frequency.rrule.indexOf('FREQ=DAILY') >= 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          textAlign="center"
          sx={{
            width: '70px',
            fontSize: 14,
            borderRight: `1px solid ${blueGrey[100]}`,
          }}
        >{((!filterTimes || filterTimes.am) && horseFeed.frequency.am && horseFeed.frequency.am.length > 0 && horseFeed.frequency.am !== '0')
          ? <>{getNumberValue(horseFeed.frequency.am)}<br />{horseFeed.quantityMeasure}</>
          : '-'}
        </Typography>
        <Typography
          textAlign="center"
          sx={{
            width: '70px',
            fontSize: 14,
            borderRight: `1px solid ${blueGrey[100]}`,
          }}
        >{((!filterTimes || filterTimes.midday) && horseFeed.frequency.midday && horseFeed.frequency.midday.length > 0 && horseFeed.frequency.midday !== '0')
          ? <>{getNumberValue(horseFeed.frequency.midday)}<br />{horseFeed.quantityMeasure}</>
          : '-'}
        </Typography>
        <Typography
          textAlign="center"
          sx={{
            width: '70px',
            fontSize: 14,
            borderRight: `1px solid ${blueGrey[100]}`,
          }}
        >{((!filterTimes || filterTimes.pm) && horseFeed.frequency.pm && horseFeed.frequency.pm.length > 0 && horseFeed.frequency.pm !== '0')
          ? <>{getNumberValue(horseFeed.frequency.pm)}<br />{horseFeed.quantityMeasure}</>
          : '-'}
        </Typography>
        <Typography
          textAlign="center"
          sx={{
            width: '70px',
            fontSize: 14,
          }}
        >
          {((!filterTimes || filterTimes.evening) && horseFeed.frequency.evening && horseFeed.frequency.evening.length > 0 && horseFeed.frequency.evening !== '0')
            ? <>{getNumberValue(horseFeed.frequency.evening)}<br />{horseFeed.quantityMeasure}</>
            : '-'}
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {`${getQuantity(horseFeed)} - ${getRruleString(horseFeed.frequency.rrule)}`}
    </Box>
  )
}

const FeedsCardComponent = ({ id }) => {
  const dispatch = useDispatch();

  // Redux state
  const {
    horseFeeds,
    isFetchingHorseFeeds,
  } = useSelector((state) => state.horse);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const { user } = useSelector((state) => state.user);

  // State
  const [openAddEditModal, setOpenAddEditModal] = React.useState(false);
  const [selectedHorseFeed, setSelectedHorseFeed] = React.useState(null);
  const [pageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [sortModel] = React.useState([
    {
      field: 'feed.order',
      sort: 'asc',
    },
    {
      field: 'feed.name',
      sort: 'asc',
    },
  ]);

  const roles = UserService.getUserRolesForTenant(
    user,
    selectedBarn.tenant,
  );
  // eslint-disable-next-line no-unused-vars
  const isAdmin = isUserAdmin(roles) || isUserOwner(roles);

  React.useEffect(() => {
    dispatch(getHorseFeeds(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  }, [id, page]);

  const handleAddClick = () => {
    setSelectedHorseFeed(null);
    setOpenAddEditModal(true);
  };

  const handleEditClick = (horseFeed) => {
    setSelectedHorseFeed(horseFeed);
    setOpenAddEditModal(true);
  };

  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    // reload
    dispatch(getHorseFeeds(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  };

  return (
    <>
      {isFetchingHorseFeeds
        ? <FeedsCardLoading />
        : (
          <Card>
            <CardHeader
              avatar={<Avatar><RestaurantIcon /></Avatar>}
              action={(
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddClick}>
                  Add Feed
                </Button>
              )}
              title="Feed"
            />
            <CardContent>
              <List
                aria-label="horse feed"
              >
                {
                  horseFeeds.content.length === 0
                    ? (
                      <ListItemButton disabled><ListItemText secondary="No horse feed specified" /></ListItemButton>
                    )
                    : horseFeeds.content.map((horseFeed) => (
                      <ListItem
                        key={horseFeed.id}
                        secondaryAction={(
                          <IconButton edge="end" aria-label="edit" onClick={() => { handleEditClick(horseFeed); }}>
                            <EditIcon />
                          </IconButton>
                        )}
                        disablePadding
                      >
                        <ListItemButton>
                          <ListItemIcon>
                            <Badge
                              badgeContent={horseFeed.feed.order}
                              color="secondary"
                              sx={{ zIndex: 100 }}
                            >
                              {getIconForFeed(horseFeed.feed, false)}
                            </Badge>
                          </ListItemIcon>
                          <ListItemText
                            primary={horseFeed.feed.name}
                            secondary={getFeedDescription(horseFeed)}
                            secondaryTypographyProps={{
                              component: 'div',
                              sx: { textWrap: 'auto' },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                }
              </List>
              <Pagination count={horseFeeds.totalPages} variant="outlined" page={page} onChange={(event, value) => { setPage(value); }} disabled={isFetchingHorseFeeds} />
            </CardContent>
          </Card>
        )}
      <AddEditModal
        open={openAddEditModal}
        horseFeed={selectedHorseFeed}
        horseId={id}
        onClose={handleCloseAddEditModal}
      />
    </>
  );
};

FeedsCardComponent.propTypes = { id: PropTypes.string.isRequired };

FeedsCardComponent.defaultProps = {};

export default FeedsCardComponent;
